import type { RuntimeMode, SettingsApi, SettingsTiles } from '@/shared/types/app.type'
import { defaultColors } from '§U/productionplan/default'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

type PayloadSetEnv = {
  api_scheme: string
  api_host: string
  api_port: string
  api_path: string
  tiles_url: string
  mode: RuntimeMode
  site_name: string
}

export type AdminReturnRoute = {
  name: string
  params: Record<string, string | string[] | undefined>
  fullPath: string
}

type StateNavigation = {
  page_header: string
  admin_return_route: AdminReturnRoute | null
}

type SetPageHeaderPayload = {
  header: string
}
type SetErrorPayload = {
  error: string
}

type StateRuntime = {
  mode: RuntimeMode
  errors: string[]
  colors: string[]
}

export const useStoreApp = defineStore('app', () => {
  // State meta. `version` is the build-time frontend version (injected by Vite,
  // see vite.config.ts); it's the baseline the deploy auto-reload check compares
  // the backend's reported version against — see useAppVersion.ts.
  const stateMeta = ref({
    version: __APP_VERSION__,
    site_name: 'Turing AS'
  })

  // State meta getters
  const version = computed<string>(() => stateMeta.value.version)

  // State api
  const stateApi = ref<SettingsApi>({
    scheme: '',
    host: '',
    port: '',
    path: ''
  })

  // State api getters
  const baseApiUrl = computed<string>(() => {
    // When API proxy is enabled, use relative path (Vite dev server proxies /api)
    if (import.meta.env.VITE_API_PROXY === 'true') {
      return stateApi.value.path
    }

    const scheme = stateApi.value.scheme.trim()
    const port = stateApi.value.port.trim()
    return (
      (scheme !== '' ? scheme + ':' : '') +
      '//' +
      stateApi.value.host +
      (port !== '' ? ':' + port : '') +
      stateApi.value.path
    )
  })

  // State tiles (Martin vector-tile server, turing-infra environments/ops)
  const stateTiles = ref<SettingsTiles>({
    url: ''
  })

  // State tiles getters: the origin without a trailing slash, or '' when no tile server is
  // configured (the maps then register no data layers). Martin's CORS allowlist covers
  // *.turing.no and localhost, so no dev proxy is needed.
  const tilesUrl = computed<string>(() => (stateTiles.value.url ?? '').trim().replace(/\/+$/, ''))

  // State runtime
  const stateRuntime = ref<StateRuntime>({
    mode: 'prod',
    errors: [],
    colors: defaultColors
  })

  // State runtime getters
  const isDev = computed((): boolean => stateRuntime.value.mode === 'dev')

  // State runtime setters
  const setError = (payload: SetErrorPayload) => {
    stateRuntime.value.errors.push(payload.error)
  }

  // State navigation
  const stateNavigation = ref<StateNavigation>({
    page_header: '',
    admin_return_route: null
  })

  // State navigation setters
  const setPageHeader = (payload: SetPageHeaderPayload) => {
    stateNavigation.value.page_header = payload.header
  }

  // The route the user came from when entering the admin area, so the
  // breadcrumb can keep showing that trail (and link back to it) while the
  // admin pages are open. Cleared when leaving the admin area.
  const setAdminReturnRoute = (route: AdminReturnRoute | null) => {
    stateNavigation.value.admin_return_route = route
  }

  // Cross state setters
  const setEnv = (payload: PayloadSetEnv) => {
    stateApi.value.scheme = payload.api_scheme
    stateApi.value.host = payload.api_host
    stateApi.value.port = payload.api_port
    stateApi.value.path = payload.api_path
    stateTiles.value.url = payload.tiles_url
    stateRuntime.value.mode = payload.mode
    stateMeta.value.site_name = payload.site_name ?? 'Turing AS'
  }

  // Check if API config has been initialized
  const isApiConfigInitialized = computed(() => {
    return stateApi.value.host !== ''
  })

  // Initialize API config from environment variables
  const initializeApiConfig = () => {
    setEnv({
      api_scheme: import.meta.env.VITE_APISCHEME || '',
      api_host: import.meta.env.VITE_APIHOST || '',
      api_port: import.meta.env.VITE_APIPORT || '',
      api_path: import.meta.env.VITE_APIPATH || '',
      tiles_url: import.meta.env.VITE_TILES_URL || '',
      mode: (import.meta.env.VITE_MODE as RuntimeMode) || 'prod',
      site_name: import.meta.env.VITE_SITE_NAME || 'Turing AS'
    })
  }

  return {
    stateMeta,

    stateApi,
    stateTiles,
    version,
    baseApiUrl,
    tilesUrl,
    isDev,

    stateRuntime,
    setError,

    stateNavigation,
    setPageHeader,
    setAdminReturnRoute,

    setEnv,
    isApiConfigInitialized,
    initializeApiConfig
  }
})
